import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "", email: "", password: "",
    role: params.get("role") === "seller" ? "seller" : "buyer",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(form);
      toast.success("Account created — check your email");
      // Always land on /pending-verification. The page itself redirects to the
      // role-appropriate dashboard the moment email_verified flips true.
      nav("/pending-verification", { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="min-h-screen grid lg:grid-cols-2 lg:grid-rows-1 items-stretch">
      <div className="flex items-center justify-center px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="register-form">
          <div>
            <h1 className="font-display font-black text-3xl text-mv-secondary">Create your account</h1>
            <p className="text-sm text-gray-500 mt-1">Join MarketVyne in seconds.</p>
          </div>

          <div className="grid grid-cols-2 gap-2">
            {["buyer", "seller"].map((r) => (
              <button key={r} type="button" onClick={() => update("role", r)}
                data-testid={`role-${r}`}
                className={`p-4 rounded-xl border-2 text-left transition-all ${form.role === r ? "border-mv-primary bg-mv-primary/5" : "border-gray-200 hover:border-gray-300"}`}>
                <div className="font-bold text-mv-secondary capitalize">{r}</div>
                <div className="text-xs text-gray-500 mt-1">
                  {r === "buyer" ? "Hire vetted experts" : "List your services"}
                </div>
              </button>
            ))}
          </div>

          <div>
            <Label htmlFor="name">Full name</Label>
            <Input id="name" data-testid="register-name" value={form.name}
              onChange={(e) => update("name", e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="register-email" type="email" value={form.email}
              onChange={(e) => update("email", e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" data-testid="register-password" type="password" minLength={6}
              value={form.password} onChange={(e) => update("password", e.target.value)} required />
          </div>
          <Button disabled={busy} type="submit" data-testid="register-submit"
            className="w-full h-12 bg-mv-primary hover:bg-mv-primary-light font-bold">
            {busy ? "Creating…" : "Create account"}
          </Button>
          <p className="text-sm text-gray-500 text-center">
            Already have an account? <Link to="/login" className="text-mv-primary font-bold">Log in</Link>
          </p>
        </form>
      </div>

      <div className="hidden lg:flex bg-mv-primary text-white px-12 py-16 flex-col justify-between relative overflow-hidden">
        <div className="absolute -bottom-32 -right-32 w-96 h-96 rounded-full bg-mv-accent/30 blur-3xl" />
        <Link to="/" className="relative inline-block" aria-label="MarketVyne home">
          <Logo variant="full" height={36} />
        </Link>
        <div className="relative">
          <h2 className="font-display font-black text-4xl">Build with experts.</h2>
          <p className="text-white/80 mt-3 max-w-md">
            Vetted sellers · Transparent risk disclosure · Escrow-style buyer protection
          </p>
        </div>
        <div className="text-xs text-white/60 relative">© MarketVyne</div>
      </div>
    </div>
  );
}
